// src/components/sales/pages/clients/ClientFormModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle } from "lucide-react";
import ModalPortal from "../../../shared/ui/ModalPortal"; // عدلي المسار حسب مكانه عندك

const empty = {
  id: "", // للعرض فقط في edit

  companyName: "",
  industry: "",
  companyType: "",

  companySize: "",
  designation: "",
  firstName: "",
  secondName: "",
  position: "",

  emailAddress: "",
  phoneNumber: "",

  emailConsent: true,
  whatsAppConsent: true,
  smsConsent: false,

  consentSource: "",
  clientStatus: "",
  dataBlock: "",
};

// suggested lists (you can edit freely)
const COMPANY_TYPES = ["establishment", "limited", "single", "corporate"];
const DESIGNATIONS = ["Dr", "Mr", "Mrs"];
const CLIENT_STATUSES = ["Lead", "Prospect", "Customer"];
const CONSENT_SOURCES = ["Sales Manual Entry", "Import", "Web Form", "Call Center", "Event"];

// Optional presets (still allow typing)
const INDUSTRIES = ["Healthcare", "Retail", "Manufacturing", "Construction", "Education", "IT", "Finance"];
const POSITIONS = ["Owner", "CEO", "General Manager", "Sales Manager", "Procurement", "HR", "Finance"];

function normalizeDataBlocks(dataBlocks) {
  const arr = Array.isArray(dataBlocks) ? dataBlocks : [];
  return arr
    .map((b) => {
      if (typeof b === "string") return { value: b, label: b };
      const v = b?.value ?? b?.name ?? b?.dataBlock ?? b?.code ?? b?.id;
      const l = b?.label ?? b?.name ?? b?.dataBlock ?? b?.code ?? String(v ?? "");
      if (!v) return null;
      return { value: String(v), label: String(l) };
    })
    .filter(Boolean);
}

function toNullableString(v) {
  const t = String(v ?? "").trim();
  return t ? t : null;
}

export default function ClientFormModal({ initialData, dataBlocks, onClose, onSubmit }) {
  const { t, i18n } = useTranslation();
  void i18n;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({ ...empty, ...(initialData || {}) }));

  useEffect(() => {
    setForm({ ...empty, ...(initialData || {}) });
  }, [initialData]);

  const title = useMemo(
    () => (initialData ? t("sales.clientForm.editTitle") : t("sales.clientForm.addTitle")),
    [initialData, t]
  );
  const blocks = useMemo(() => normalizeDataBlocks(dataBlocks), [dataBlocks]);

  const change = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const validate = () => {
    if (!form.companyName?.trim()) return t("sales.clientForm.validation.companyNameRequired");
    if (!form.companyType?.trim()) return t("sales.clientForm.validation.companyTypeRequired");
    if (!form.designation?.trim()) return t("sales.clientForm.validation.designationRequired");
    if (!form.firstName?.trim()) return t("sales.clientForm.validation.firstNameRequired");
    if (!form.emailAddress?.trim()) return t("sales.clientForm.validation.emailRequired");
    if (!form.phoneNumber?.trim()) return t("sales.clientForm.validation.phoneRequired");
    if (!form.clientStatus?.trim()) return t("sales.clientForm.validation.clientStatusRequired");
    return "";
  };

  const submit = async () => {
    const err = validate();
    if (err) return alert(err);

    setSaving(true);
    try {
      // Match DTO exactly (no consentTimestamp)
      const payload = {
        companyName: form.companyName.trim(),
        industry: toNullableString(form.industry),

        companyType: form.companyType.trim(),
        companySize: toNullableString(form.companySize),

        designation: form.designation.trim(),
        firstName: form.firstName.trim(),
        secondName: toNullableString(form.secondName),

        position: toNullableString(form.position),

        emailAddress: form.emailAddress.trim(),
        phoneNumber: form.phoneNumber.trim(),

        emailConsent: !!form.emailConsent,
        whatsAppConsent: !!form.whatsAppConsent,
        smsConsent: !!form.smsConsent,

        consentSource: toNullableString(form.consentSource),

        clientStatus: form.clientStatus.trim(),
        dataBlock: toNullableString(form.dataBlock), // OPTIONAL
      };

      await onSubmit?.(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onMouseDown={saving ? undefined : onClose}
      />

      {/* Centered modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-5xl bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden my-10"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 disabled:opacity-50"
              disabled={saving}
              title={t("common.close")}
              type="button"
            >
              <XCircle className="w-7 h-7" />
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto text-white">
            {initialData?.id ? (
              <div className="mb-4">
                <Box label={t("sales.clientForm.clientIdLabel")}>
                  <input value={initialData.id} className={inputCls + " opacity-80"} disabled />
                </Box>
              </div>
            ) : null}

            <div className="grid md:grid-cols-2 gap-4">
              <Box label={t("sales.clientForm.companyNameLabel")}>
                <input value={form.companyName} onChange={change("companyName")} className={inputCls} disabled={saving} />
              </Box>

              <Box label={t("sales.clientForm.industryLabel")}>
                <SelectWithOther
                  value={form.industry}
                  onChange={(v) => setForm((p) => ({ ...p, industry: v }))}
                  options={INDUSTRIES}
                  disabled={saving}
                  placeholder={t("sales.clientForm.industryPlaceholder")}
                />
              </Box>

              <Box label={t("sales.clientForm.companyTypeLabel")}>
                <Select
                  value={form.companyType}
                  onChange={change("companyType")}
                  options={COMPANY_TYPES}
                  disabled={saving}
                  placeholder={t("sales.clientForm.companyTypePlaceholder")}
                  optionLabelKey="sales.clientForm.companyTypes"
                />
              </Box>

              <Box label={t("sales.clientForm.companySizeLabel")}>
                <input
                  value={form.companySize}
                  onChange={change("companySize")}
                  className={inputCls}
                  disabled={saving}
                  placeholder={t("sales.clientForm.optional")}
                />
              </Box>

              <Box label={t("sales.clientForm.designationLabel")}>
                <Select
                  value={form.designation}
                  onChange={change("designation")}
                  options={DESIGNATIONS}
                  disabled={saving}
                  placeholder={t("sales.clientForm.designationPlaceholder")}
                  optionLabelKey="sales.clientForm.designations"
                />
              </Box>

              <Box label={t("sales.clientForm.positionLabel")}>
                <SelectWithOther
                  value={form.position}
                  onChange={(v) => setForm((p) => ({ ...p, position: v }))}
                  options={POSITIONS}
                  disabled={saving}
                  placeholder={t("sales.clientForm.positionPlaceholder")}
                  optionLabelKey="sales.clientForm.positions"
                />
              </Box>

              <Box label={t("sales.clientForm.firstNameLabel")}>
                <input value={form.firstName} onChange={change("firstName")} className={inputCls} disabled={saving} />
              </Box>

              <Box label={t("sales.clientForm.secondNameLabel")}>
                <input value={form.secondName} onChange={change("secondName")} className={inputCls} disabled={saving} />
              </Box>

              <Box label={t("sales.clientForm.emailLabel")}>
                <input
                  type="email"
                  value={form.emailAddress}
                  onChange={change("emailAddress")}
                  className={inputCls}
                  disabled={saving}
                />
              </Box>

              <Box label={t("sales.clientForm.phoneLabel")}>
                <input value={form.phoneNumber} onChange={change("phoneNumber")} className={inputCls} disabled={saving} />
              </Box>

              <Box label={t("sales.clientForm.clientStatusLabel")}>
                <Select
                  value={form.clientStatus}
                  onChange={change("clientStatus")}
                  options={CLIENT_STATUSES}
                  disabled={saving}
                  placeholder={t("sales.clientForm.clientStatusPlaceholder")}
                  optionLabelKey="sales.clientForm.clientStatuses"
                />
              </Box>

              <Box label={t("sales.clientForm.dataBlockLabel")}>
                <input
                  list="dataBlocksList"
                  value={form.dataBlock || ""}
                  onChange={change("dataBlock")}
                  className={inputCls}
                  disabled={saving}
                  placeholder={
                    blocks.length
                      ? t("sales.clientForm.dataBlockPlaceholder")
                      : t("sales.clientForm.optional")
                  }
                />

                {blocks.length ? (
                  <datalist id="dataBlocksList">
                    {blocks.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </datalist>
                ) : null}
              </Box>

              <div className="md:col-span-2 bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl">
                <div className="grid md:grid-cols-3 gap-3">
                  <Toggle label={t("sales.clientForm.emailConsent")} checked={!!form.emailConsent} onChange={change("emailConsent")} disabled={saving} />
                  <Toggle label={t("sales.clientForm.whatsAppConsent")} checked={!!form.whatsAppConsent} onChange={change("whatsAppConsent")} disabled={saving} />
                  <Toggle label={t("sales.clientForm.smsConsent")} checked={!!form.smsConsent} onChange={change("smsConsent")} disabled={saving} />
                </div>

                <div className="mt-4">
                  <label className="text-sm text-gray-200">{t("sales.clientForm.consentSourceLabel")}</label>
                  <div className="mt-2">
                    <SelectWithOther
                      value={form.consentSource}
                      onChange={(v) => setForm((p) => ({ ...p, consentSource: v }))}
                      options={CONSENT_SOURCES}
                      disabled={saving}
                      placeholder={t("sales.clientForm.consentSourcePlaceholder")}
                      optionLabelKey="sales.clientForm.consentSources"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={submit}
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                type="button"
              >
                <CheckCircle className="w-5 h-5" />
                {saving ? t("sales.clientForm.saving") : t("sales.clientForm.save")}
              </button>

              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
                disabled={saving}
                type="button"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Box({ label, children }) {
  return (
    <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl">
      <label className="text-sm text-gray-200">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, disabled }) {
  return (
    <label className={`flex items-center gap-2 ${disabled ? "opacity-70" : ""}`}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="w-5 h-5" />
      <span>{label}</span>
    </label>
  );
}

function Select({ value, onChange, options, disabled, placeholder, optionLabelKey }) {
  const { t } = useTranslation();
  return (
    <select value={value || ""} onChange={onChange} disabled={disabled} className={inputCls}>
      <option value="" className="text-black">
        {placeholder || t("sales.clientForm.selectPlaceholder")}
      </option>
      {(options || []).map((o) => (
        <option key={o} value={o} className="text-black">
          {optionLabelKey ? t(`${optionLabelKey}.${o}`) : o}
        </option>
      ))}
    </select>
  );
}

function SelectWithOther({ value, onChange, options, disabled, placeholder, optionLabelKey }) {
  const { t } = useTranslation();
  const isCustom = !!value && !(options || []).includes(value);
  const [otherMode, setOtherMode] = React.useState(isCustom);

  React.useEffect(() => {
    setOtherMode(isCustom);
  }, [isCustom]);

  const selectedValue = otherMode ? "__OTHER__" : value || "";

  return (
    <div className="space-y-2">
      <select
        value={selectedValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__OTHER__") {
            setOtherMode(true);
            if (!isCustom) onChange("");
          } else {
            setOtherMode(false);
            onChange(v);
          }
        }}
        disabled={disabled}
        className={inputCls}
      >
        <option value="" className="text-black">
          {placeholder || t("sales.clientForm.selectPlaceholder")}
        </option>
        {(options || []).map((o) => (
          <option key={o} value={o} className="text-black">
            {optionLabelKey ? t(`${optionLabelKey}.${o}`) : o}
          </option>
        ))}
        <option value="__OTHER__" className="text-black">
          {t("sales.clientForm.otherOption")}
        </option>
      </select>

      {otherMode && (
        <input
          value={isCustom ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          disabled={disabled}
          placeholder={t("sales.clientForm.customValuePlaceholder")}
        />
      )}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white placeholder:text-white/50 border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
